import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, Select, Checkbox, Col, Row, Divider, message, Alert, Button } from 'antd';
import dayjs from 'dayjs';
import { EditEstateOwnerPayload, Estate, EstateOwner, UpdateAddress } from '../../types';
import { editEstateOwner } from './estateService';
import { resendUserVerification } from '../verify/verify.service';
import { US_STATES } from './usstates';

import { isSyntheticEmail } from './estatedetails';
import { formatSSN, SSN_RULE } from './ssn';

const { Option } = Select;

interface EditOwnerModalProps {
  open: boolean;
  estateId: string;
  owner?: EstateOwner | null;
  onCancel: () => void;
  onSubmit: (estate: Estate) => void;
}

/**
 * Edits the estate owner on its own.
 */
const EditOwnerModal: React.FC<EditOwnerModalProps> = ({
  open,
  estateId,
  owner,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isDeceased, setIsDeceased] = useState(false);
  const [sameAsDomicileForMailing, setSameAsDomicileForMailing] = useState(true);
  const [sameAsDomicileForPresent, setSameAsDomicileForPresent] = useState(true);
  const [resendingVerification, setResendingVerification] = useState(false);

  const handleResendVerificationClick = async () => {
    setResendingVerification(true);
    try {
      const res = await resendUserVerification();
      Modal.info({
        title: 'Verification Email Sent',
        content: res.message || `A verification email has been resent to ${res.pendingEmail || owner?.pendingEmail}. Please check inbox.`,
        okText: 'Understood',
      });
    } catch (err: any) {
      message.error(err.response?.data?.message || err.message || 'Failed to resend verification email.');
    } finally {
      setResendingVerification(false);
    }
  };

  const [domicileUid, setDomicileUid] = useState<string | undefined>(undefined);
  const [mailingUid, setMailingUid] = useState<string | undefined>(undefined);
  const [presentUid, setPresentUid] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) return;

    const addressesList = (owner as any)?.addresses || [];
    const domicile = addressesList.find((a: any) => a.addressType === 'domicile') || addressesList[0] || {};
    const mailing = addressesList.find((a: any) => a.addressType === 'mailing');
    const present = addressesList.find((a: any) => a.addressType === 'present' || a.addressType === 'billing');

    setDomicileUid(domicile.uid);
    setMailingUid(mailing?.uid);
    setPresentUid(present?.uid);

    const isMailingSame = !mailing || !mailing.addressLine1 || (
      mailing.addressLine1 === domicile.addressLine1 &&
      mailing.city === domicile.city &&
      mailing.state === domicile.state &&
      mailing.zipCode === domicile.zipCode
    );

    const isPresentSame = !present || !present.addressLine1 || (
      present.addressLine1 === domicile.addressLine1 &&
      present.city === domicile.city &&
      present.state === domicile.state &&
      present.zipCode === domicile.zipCode
    );

    setSameAsDomicileForMailing(isMailingSame);
    setSameAsDomicileForPresent(isPresentSame);

    const deceasedVal = owner?.deceased || (owner as any)?.isDeceased || false;
    setIsDeceased(deceasedVal);

    form.setFieldsValue({
      email: isSyntheticEmail(owner?.email) ? '' : (owner?.email || ''),
      phone: owner?.phone || '',
      prefix: owner?.prefix || '',
      firstName: owner?.firstName || '',
      middleName: owner?.middleName || '',
      lastName: owner?.lastName || '',
      suffix: owner?.suffix || '',
      sex: owner?.sex || undefined,
      dob: owner?.dob ? dayjs(owner.dob) : null,
      maritalStatus: owner?.maritalStatus || undefined,
      citizenship: (owner as any)?.citizenship || '',
      isDeceased: deceasedVal,
      dateOfDeath: owner?.dateOfDeath ? dayjs(owner.dateOfDeath) : null,
      placeOfDeathName: (owner as any)?.placeOfDeathName || '',
      placeOfDeathAddress: (owner as any)?.placeOfDeathAddress || '',
      ssn: owner?.ssn || '',

      // Domicile address
      domicileAddressLine1: domicile.addressLine1 || '',
      domicileAddressLine2: domicile.addressLine2 || '',
      domicileCity: domicile.city || '',
      domicileState: domicile.state || undefined,
      domicileZipCode: domicile.zipCode || '',
      domicileCounty: domicile.county || '',

      // Mailing address
      mailingAddressLine1: isMailingSame ? (domicile.addressLine1 || '') : (mailing?.addressLine1 || ''),
      mailingAddressLine2: isMailingSame ? (domicile.addressLine2 || '') : (mailing?.addressLine2 || ''),
      mailingCity: isMailingSame ? (domicile.city || '') : (mailing?.city || ''),
      mailingState: isMailingSame ? (domicile.state || undefined) : (mailing?.state || undefined),
      mailingZipCode: isMailingSame ? (domicile.zipCode || '') : (mailing?.zipCode || ''),
      mailingCounty: isMailingSame ? (domicile.county || '') : (mailing?.county || ''),

      // Present address
      presentAddressLine1: isPresentSame ? (domicile.addressLine1 || '') : (present?.addressLine1 || ''),
      presentAddressLine2: isPresentSame ? (domicile.addressLine2 || '') : (present?.addressLine2 || ''),
      presentCity: isPresentSame ? (domicile.city || '') : (present?.city || ''),
      presentState: isPresentSame ? (domicile.state || undefined) : (present?.state || undefined),
      presentZipCode: isPresentSame ? (domicile.zipCode || '') : (present?.zipCode || ''),
      presentCounty: isPresentSame ? (domicile.county || '') : (present?.county || ''),
    });
  }, [open, owner, form]);

  const handleMailingSameChange = (e: any) => {
    const checked = e.target.checked;
    setSameAsDomicileForMailing(checked);
    if (checked) {
      form.setFieldsValue({
        mailingAddressLine1: form.getFieldValue('domicileAddressLine1'),
        mailingAddressLine2: form.getFieldValue('domicileAddressLine2'),
        mailingCity: form.getFieldValue('domicileCity'),
        mailingState: form.getFieldValue('domicileState'),
        mailingZipCode: form.getFieldValue('domicileZipCode'),
        mailingCounty: form.getFieldValue('domicileCounty'),
      });
    }
  };

  const handlePresentSameChange = (e: any) => {
    const checked = e.target.checked;
    setSameAsDomicileForPresent(checked);
    if (checked) {
      form.setFieldsValue({
        presentAddressLine1: form.getFieldValue('domicileAddressLine1'),
        presentAddressLine2: form.getFieldValue('domicileAddressLine2'),
        presentCity: form.getFieldValue('domicileCity'),
        presentState: form.getFieldValue('domicileState'),
        presentZipCode: form.getFieldValue('domicileZipCode'),
        presentCounty: form.getFieldValue('domicileCounty'),
      });
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const domicileAddress: UpdateAddress = {
        addressType: 'domicile',
        uid: domicileUid,
        addressLine1: values.domicileAddressLine1 || '',
        addressLine2: values.domicileAddressLine2 || '',
        city: values.domicileCity || '',
        state: values.domicileState || '',
        zipCode: values.domicileZipCode || '',
        county: values.domicileCounty || '',
      };

      const mailingAddress: UpdateAddress = {
        addressType: 'mailing',
        uid: mailingUid,
        addressLine1: sameAsDomicileForMailing ? values.domicileAddressLine1 || '' : values.mailingAddressLine1 || '',
        addressLine2: sameAsDomicileForMailing ? values.domicileAddressLine2 || '' : values.mailingAddressLine2 || '',
        city: sameAsDomicileForMailing ? values.domicileCity || '' : values.mailingCity || '',
        state: sameAsDomicileForMailing ? values.domicileState || '' : values.mailingState || '',
        zipCode: sameAsDomicileForMailing ? values.domicileZipCode || '' : values.mailingZipCode || '',
        county: sameAsDomicileForMailing ? values.domicileCounty || '' : values.mailingCounty || '',
      };

      const presentAddress: UpdateAddress = {
        addressType: 'present',
        uid: presentUid,
        addressLine1: sameAsDomicileForPresent ? values.domicileAddressLine1 || '' : values.presentAddressLine1 || '',
        addressLine2: sameAsDomicileForPresent ? values.domicileAddressLine2 || '' : values.presentAddressLine2 || '',
        city: sameAsDomicileForPresent ? values.domicileCity || '' : values.presentCity || '',
        state: sameAsDomicileForPresent ? values.domicileState || '' : values.presentState || '',
        zipCode: sameAsDomicileForPresent ? values.domicileZipCode || '' : values.presentZipCode || '',
        county: sameAsDomicileForPresent ? values.domicileCounty || '' : values.presentCounty || '',
      };

      const payload: EditEstateOwnerPayload = {
        email: values.email || undefined,
        phone: values.phone || undefined,
        prefix: values.prefix || '',
        firstName: values.firstName,
        middleName: values.middleName || '',
        lastName: values.lastName,
        suffix: values.suffix || '',
        sex: values.sex || '',
        dob: values.dob ? values.dob.format('YYYY-MM-DD') : undefined,
        maritalStatus: values.maritalStatus || undefined,
        citizenship: values.citizenship || undefined,
        isDeceased: isDeceased,
        dateOfDeath: isDeceased && values.dateOfDeath ? values.dateOfDeath.format('YYYY-MM-DD') : undefined,
        placeOfDeathName: isDeceased ? values.placeOfDeathName : undefined,
        placeOfDeathAddress: isDeceased ? values.placeOfDeathAddress : undefined,
        ssn: values.ssn && values.ssn.includes('*') ? undefined : values.ssn,

        // Backwards compatibility primary address fields
        addressLine1: values.domicileAddressLine1,
        addressLine2: values.domicileAddressLine2,
        city: values.domicileCity,
        state: values.domicileState,
        zipCode: values.domicileZipCode,
        county: values.domicileCounty,

        addresses: [domicileAddress, mailingAddress, presentAddress],
      };

      const estate = await editEstateOwner(estateId, payload);
      onSubmit(estate);
      if (estate.estateOwner?.pendingEmail) {
        Modal.info({
          title: 'Email Verification Required',
          content: (
            <div>
              <p>Owner details updated successfully.</p>
              <p>
                A verification email has been sent to <strong>{estate.estateOwner.pendingEmail}</strong>.
                The email address will remain unverified until confirmed via the link in the email.
              </p>
            </div>
          ),
          okText: 'Understood',
        });
      } else {
        message.success('Owner updated successfully.');
      }
      onCancel();
    } catch (error: any) {
      if (error?.errorFields) return; // validation, already shown on the fields
      console.error(error);
      message.error(error.message || 'Failed to update owner.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Owner"
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={loading}
      width={720}
    >
      <Form form={form} layout="vertical">
        {owner?.pendingEmail && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: '1.5rem' }}
            message="Email Verification Pending"
            description={
              <div>
                <p style={{ margin: '0 0 8px 0' }}>
                  Email change to <strong>{owner.pendingEmail}</strong> is unverified. Current active email is <strong>{owner.email}</strong>.
                </p>
                <Button
                  size="small"
                  type="primary"
                  ghost
                  loading={resendingVerification}
                  onClick={handleResendVerificationClick}
                >
                  Resend Verification Email
                </Button>
              </div>
            }
          />
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Email Address"
              name="email"
              rules={[{ type: 'email', message: 'Please enter a valid email address!' }]}
              help={owner?.pendingEmail ? `Pending Verification: ${owner.pendingEmail} (Verification email sent)` : undefined}
              validateStatus={owner?.pendingEmail ? 'warning' : undefined}
            >
              <Input placeholder="Enter owner's email address" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Phone Number" name="phone">
              <Input placeholder="Enter phone number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="Prefix" name="prefix">
              <Input placeholder="e.g., Mr., Ms." />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Please enter the first name!' }]}
            >
              <Input placeholder="Enter first name" />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item label="Middle Name" name="middleName">
              <Input placeholder="Enter middle name (optional)" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Please enter the last name!' }]}
            >
              <Input placeholder="Enter last name" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Suffix" name="suffix">
              <Input placeholder="e.g., Jr., Sr." />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Sex" name="sex">
              <Select placeholder="Select sex" allowClear>
                <Option value="male">Male</Option>
                <Option value="female">Female</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Date of Birth" name="dob">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select date of birth" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Marital Status" name="maritalStatus">
              <Select placeholder="Select status" allowClear>
                <Option value="single">Single</Option>
                <Option value="married">Married</Option>
                <Option value="divorced">Divorced</Option>
                <Option value="widowed">Widowed</Option>
                <Option value="separated">Separated</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Citizenship" name="citizenship">
              <Input placeholder="e.g., USA" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Social Security Number (SSN)"
          name="ssn"
          normalize={formatSSN}
          rules={[SSN_RULE]}
          extra="For security, SSNs are stored encrypted and displayed masked (***-**-1234)."
        >
          <Input placeholder="123-45-6789" maxLength={11} />
        </Form.Item>

        <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
          Domicile Address
        </Divider>
        <Form.Item label="Address Line 1" name="domicileAddressLine1">
          <Input placeholder="Enter domicile address line 1" />
        </Form.Item>
        <Form.Item label="Address Line 2" name="domicileAddressLine2">
          <Input placeholder="Enter domicile address line 2 (optional)" />
        </Form.Item>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="City" name="domicileCity">
              <Input placeholder="Enter city" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="State" name="domicileState">
              <Select placeholder="Select state" showSearch optionFilterProp="children" allowClear>
                {US_STATES.map((state) => (
                  <Option key={state.code} value={state.code}>
                    {state.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Zip Code" name="domicileZipCode">
              <Input placeholder="Enter zip code" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="County" name="domicileCounty">
          <Input placeholder="Enter county" />
        </Form.Item>

        <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
          Mailing Address
        </Divider>
        <Form.Item>
          <Checkbox checked={sameAsDomicileForMailing} onChange={handleMailingSameChange}>
            Same as Domicile Address
          </Checkbox>
        </Form.Item>
        {!sameAsDomicileForMailing && (
          <>
            <Form.Item label="Address Line 1" name="mailingAddressLine1">
              <Input placeholder="Enter mailing address line 1" />
            </Form.Item>
            <Form.Item label="Address Line 2" name="mailingAddressLine2">
              <Input placeholder="Enter mailing address line 2 (optional)" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="City" name="mailingCity">
                  <Input placeholder="Enter city" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="State" name="mailingState">
                  <Select placeholder="Select state" showSearch optionFilterProp="children" allowClear>
                    {US_STATES.map((state) => (
                      <Option key={state.code} value={state.code}>
                        {state.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Zip Code" name="mailingZipCode">
                  <Input placeholder="Enter zip code" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="County" name="mailingCounty">
              <Input placeholder="Enter county" />
            </Form.Item>
          </>
        )}

        <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
          Present / Billing Address
        </Divider>
        <Form.Item>
          <Checkbox checked={sameAsDomicileForPresent} onChange={handlePresentSameChange}>
            Same as Domicile Address
          </Checkbox>
        </Form.Item>
        {!sameAsDomicileForPresent && (
          <>
            <Form.Item label="Address Line 1" name="presentAddressLine1">
              <Input placeholder="Enter present/billing address line 1" />
            </Form.Item>
            <Form.Item label="Address Line 2" name="presentAddressLine2">
              <Input placeholder="Enter present/billing address line 2 (optional)" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="City" name="presentCity">
                  <Input placeholder="Enter city" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="State" name="presentState">
                  <Select placeholder="Select state" showSearch optionFilterProp="children" allowClear>
                    {US_STATES.map((state) => (
                      <Option key={state.code} value={state.code}>
                        {state.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Zip Code" name="presentZipCode">
                  <Input placeholder="Enter zip code" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="County" name="presentCounty">
              <Input placeholder="Enter county" />
            </Form.Item>
          </>
        )}

        <Divider />

        <Form.Item name="isDeceased" valuePropName="checked">
          <Checkbox onChange={(e) => setIsDeceased(e.target.checked)}>Is Deceased</Checkbox>
        </Form.Item>
        {isDeceased && (
          <>
            <Form.Item label="Date of Death" name="dateOfDeath">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select date of death" />
            </Form.Item>
            <Form.Item label="Place of Death Name" name="placeOfDeathName">
              <Input placeholder="e.g. Hospital or Facility name" />
            </Form.Item>
            <Form.Item label="Place of Death Address" name="placeOfDeathAddress">
              <Input placeholder="Enter place of death address" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EditOwnerModal;
